<template>
  <div class="w-full flex items-center justify-center gap-2">
    <UseExample :src="exampleSrc" />
    <div class="glass-frosted relative rounded-8xl w-fit h-67.5 p-4">
      <div class="p-2">
        <Keyboard
          key-class="flex items-center justify-center gap-1 bg-key text-black-white font-[Barlow] text-xs-semibold! whitespace-nowrap transition-colors cursor-pointer hover:text-text-glass-default active:bg-white active:text-brand-blue2 disabled:text-glass-disabled disabled:cursor-not-allowed disabled:pointer-events-none"
          :keyboard-config="keyboardConfig!"
          :native-event-names="sourceKeyboardEventNames"
          :gap="6"
          @key-event="handleSourceKeyEvent"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, inject } from 'vue'
  import { Keyboard } from '@rapoo/element'
  import { useLocaleStore } from '@/stores'
  import { setDragData, DragType } from '@/utils/dnd'
  import UseExample from '@/components/UseExample.vue'
  import { useDragImage } from '@/views/device/keyboard/keys/composables/useDragImage'
  import { fullSize108 } from '@/utils/fullSize108'
  import { KEYCODE_KEYWORDS_MAP, getLocalizedField } from '../utils/keyMapSetting'
  import { SelectedKeySymbol, UpdateSelectedKeyMappingSymbol } from '../types'
  import useExampleZhCN from '@/assets/images/recording/mouse/keys-zhCN.gif'
  import useExampleEnUS from '@/assets/images/recording/mouse/keys-enUS.gif'

  import type {
    KeyEventName,
    KeyboardKeyEventPayload,
    KeyboardConfig,
    KeyConfig,
  } from '@rapoo/element'
  import type { KeyChangePayload, UpdateSelectedKeyMapping } from '../types'

  const { language } = useLocaleStore()

  const selectedKey = inject(SelectedKeySymbol)
  const updateSelectedKeyMapping = inject<UpdateSelectedKeyMapping>(UpdateSelectedKeyMappingSymbol)

  const exampleSrc = computed(() => (language === 'zh-CN' ? useExampleZhCN : useExampleEnUS))

  const keyboardConfig = computed<KeyboardConfig | null>(() => {
    return {
      ...fullSize108,
      keys: fullSize108.keys.map((row) =>
        row
          .map((key) => {
            const { w, h, x, y, keywords, ...rest } = key
            const config: KeyConfig = KEYCODE_KEYWORDS_MAP.get(keywords)!

            return {
              ...rest,
              ...config,
              rect: { w, h, x, y },
            }
          })
          .filter((key) => key.keywords !== 'Fn'),
      ),
    }
  })

  const sourceKeyboardEventNames: KeyEventName[] = ['click', 'dragstart', 'dragend']
  const { setDragImage } = useDragImage()

  /**
   * 构建改键 payload。
   * 键盘字符面板的按键在 keycode.json 中 type=1（Keyboard），
   * 对应 SDK type=0x00，keyType 为 'keyboard'。
   */
  const buildKeyChangePayload = (data: KeyConfig, targetKeywords: string): KeyChangePayload => ({
    keywords: targetKeywords,
    keyType: 'keyboard',
    target: data,
    raw: data,
  })

  const handleSourceKeyEvent = (ctx: KeyboardKeyEventPayload) => {
    const { data, eventName, nativeEvent } = ctx

    if (eventName === 'click') {
      if (!selectedKey?.value || !updateSelectedKeyMapping) return

      const payload = buildKeyChangePayload(data, selectedKey.value.keywords)
      updateSelectedKeyMapping(payload)
    }

    if (eventName === 'dragstart') {
      const event = nativeEvent as DragEvent
      const dataTransfer = event.dataTransfer
      if (!dataTransfer) return

      // 拖拽时 keywords 留空，由目标组件在 drop 时填入
      const dragData = buildKeyChangePayload(data, '')
      setDragData(dataTransfer, DragType.KEY_MAPPING, dragData)

      setDragImage(event, {
        text: getLocalizedField(data.alias || data.name),
      })
    }
  }
</script>

<style scoped></style>
